// ─── Final Export Preview Page ────────────────────────────────────────────────
// Renders a pixel-perfect print-layout preview showing exactly what the
// exported DOCX/PDF will look like, catching page breaks, figure overflow,
// spacing issues, and malformed headings before export.

import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ChevronLeft, Loader2, AlertTriangle, FileType, Download, Printer } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { composeThesisDocument, renderComposedChapterHtml } from '../../utils/exportCompositionEngine'
import { A4_PAGE_CSS } from '../../utils/a4PaginationEngine'
import { loadLatestSnapshot, snapshotToChapters, snapshotToRegistries } from '../../utils/snapshotEngine'
import { getProfile, interpolate } from '../../lib/universityProfiles'
import toast from 'react-hot-toast'

// ── Page simulation CSS ───────────────────────────────────────────────────────

// Alias: preview-page maps to a4-page for consistent styling
const PAGE_CSS = A4_PAGE_CSS + `
  .preview-page {
    width: 794px;
    height: 1123px;
    background: white;
    box-shadow: 0 4px 24px rgba(0,0,0,0.20);
    margin: 0 auto 2rem;
    position: relative;
    overflow: hidden;
    flex-shrink: 0;
    border: 1px solid #d1d5db;
    font-family: "Times New Roman", Georgia, serif;
    font-size: 12pt;
    line-height: 2;
    color: #000;
    box-sizing: border-box;
    page-break-after: always;
  }
  .preview-page.overflowing { border: 2px solid #ef4444 !important; }
  .page-overflow-warning {
    position: absolute; top: 0; left: 0; right: 0; background: #ef4444;
    color: white; font-size: 10px; font-family: sans-serif; padding: 2px 8px;
    font-weight: 700; z-index: 10;
  }
  .page-header {
    position: absolute; top: 48px; left: 144px; right: 96px;
    font-size: 9pt; color: #888; text-align: right; font-family: "Times New Roman", serif;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .page-footer {
    position: absolute; bottom: 48px; left: 144px; right: 96px;
    font-size: 9pt; color: #666; text-align: center; font-family: "Times New Roman", serif;
  }
  .preview-content {
    position: absolute;
    top: 134px; left: 144px; right: 96px; bottom: 134px;
    overflow: hidden;
  }
  @media print {
    .preview-shell { display: none; }
    .preview-page { margin: 0; box-shadow: none; border: none; page-break-after: always; }
  }
`

// ── Chapter HTML renderer ─────────────────────────────────────────────────────

function renderChapterForPreview(ch, figureRegistry, tableRegistry) {
  return renderComposedChapterHtml(ch.content || '', figureRegistry, tableRegistry, 'preview')
}

// ── Page component ────────────────────────────────────────────────────────────

function PreviewPage({ number, title, children, isOverflowing }) {
  return (
    <div className={`preview-page${isOverflowing ? ' overflowing' : ''}`}>
      {isOverflowing && (
        <div className="page-overflow-warning">Content may overflow this page boundary</div>
      )}
      <div className="page-header">{title}</div>
      <div className="preview-content" dangerouslySetInnerHTML={{ __html: children }} />
      <div className="page-footer">{number}</div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ThesisExportPreviewPage() {
  const { id: thesisId } = useParams()

  const [loading,   setLoading]   = useState(true)
  const [thesis,    setThesis]    = useState(null)
  const [chapters,  setChapters]  = useState([])
  const [fm,        setFm]        = useState(null)
  const [figures,   setFigures]   = useState(new Map())
  const [tables,    setTables]    = useState(new Map())
  const [snapshot,  setSnapshot]  = useState(null)
  const [warnings,  setWarnings]  = useState([])
  const [pages,     setPages]     = useState([])

  useEffect(() => {
    if (!thesisId) return
    const load = async () => {
      try {
        // Try to use latest snapshot first; fall back to live data
        const snap = await loadLatestSnapshot(thesisId).catch(() => null)

        let t, chs, f, dbFigs
        if (snap) {
          setSnapshot(snap)
          t    = snap.snapshot_data?.thesis
          f    = snap.snapshot_data?.fm
          chs  = snapshotToChapters(snap)
          const regs = snapshotToRegistries(snap)
          setFigures(regs.figures)
          setTables(regs.tables)
          dbFigs = []
        } else {
          const [{ data: tv }, { data: cv }, { data: fv }, { data: figsV }] = await Promise.all([
            supabase.from('theses').select('*').eq('id', thesisId).maybeSingle(),
            supabase.from('chapters').select('*').eq('thesis_id', thesisId).order('chapter_number'),
            supabase.from('thesis_front_matter').select('*').eq('thesis_id', thesisId).maybeSingle(),
            supabase.from('thesis_figures').select('*').eq('thesis_id', thesisId).order('display_order'),
          ])
          t    = tv; chs = cv || []; f = fv
          const composed = composeThesisDocument({ thesis: t, chapters: chs })
          setFigures(composed.figureRegistry)
          setTables(composed.tableRegistry)
        }

        setThesis(t)
        setFm(f)
        const sorted = [...(chs || [])].sort((a, b) => (a.chapter_number || 0) - (b.chapter_number || 0))
        setChapters(sorted)

        // Build pages and run overflow checks
        buildPages(sorted, t, f, figures, tables)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [thesisId])

  function buildPages(chs, t, f, fMap, tMap) {
    const pg = []
    const w  = []
    let pageNum = 1

    // Front matter pages (simplified)
    const fmTitle = t?.title || 'PhD Thesis'
    pg.push({ num: pageNum++, title: fmTitle, html: buildFrontMatterPreview(f, t), type: 'frontmatter' })
    pg.push({ num: pageNum++, title: 'Table of Contents', html: buildTOCPreview(chs), type: 'toc' })

    const lofHtml = buildLOFPreview(fMap)
    if (lofHtml) pg.push({ num: pageNum++, title: 'List of Figures', html: lofHtml, type: 'lof' })

    const lotHtml = buildLOTPreview(tMap)
    if (lotHtml) pg.push({ num: pageNum++, title: 'List of Tables', html: lotHtml, type: 'lot' })

    // Chapters
    for (const ch of chs) {
      const html  = renderChapterForPreview(ch, fMap, tMap)
      const charCount = html.replace(/<[^>]+>/g, '').length
      const estimatedPages = Math.ceil(charCount / 3000) // ~3000 chars per page

      for (let p = 0; p < Math.max(1, estimatedPages); p++) {
        pg.push({
          num:   pageNum++,
          title: `${fmTitle} — Ch. ${ch.chapter_number}`,
          html:  p === 0 ? `<h1 class="chapter-heading">CHAPTER ${ch.chapter_number}: ${ch.title}</h1>${html}` : html,
          type:  'chapter',
          chapterNum: ch.chapter_number,
          isOverflowing: charCount > 4500 && estimatedPages > 5 && p === 0, // flag very dense pages
        })
      }

      if (charCount < 200 && (ch.content || '').trim().length > 0) {
        w.push({ type: 'SHORT_CHAPTER', message: `Chapter ${ch.chapter_number}: "${ch.title}" appears to have very little content in the preview.` })
      }
    }

    setPages(pg)
    setWarnings(w)
  }

  const profile = getProfile(fm?.university_profile || 'kennedy')

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <Loader2 style={{ width: 24, height: 24, color: '#2563eb', animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', background: '#374151' }}>
      <style>{PAGE_CSS + `@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Top bar */}
      <div className="preview-shell" style={{ position: 'sticky', top: 0, zIndex: 20, background: '#1f2937', color: '#f9fafb', padding: '0.75rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #374151' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Link to={`/app/thesis/${thesisId}/export`} style={{ color: '#9ca3af', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
            <ChevronLeft style={{ width: 14, height: 14 }} /> Export
          </Link>
          <span style={{ fontSize: 14, fontWeight: 600 }}>Export Preview — {thesis?.title}</span>
          {snapshot && <span style={{ fontSize: 11, background: '#065f46', color: '#6ee7b7', padding: '2px 8px', borderRadius: 4 }}>Snapshot v{snapshot.version_number}</span>}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {warnings.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#fbbf24' }}>
              <AlertTriangle style={{ width: 14, height: 14 }} />
              {warnings.length} warning(s)
            </div>
          )}
          <span style={{ fontSize: 12, color: '#9ca3af' }}>{pages.length} pages</span>
          <button
            onClick={() => window.print()}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0.4rem 0.75rem', borderRadius: 6, border: '1px solid #4b5563', background: '#374151', color: '#f9fafb', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
          >
            <Printer style={{ width: 13, height: 13 }} /> Print / PDF
          </button>
          <Link
            to={`/app/thesis/${thesisId}/export`}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0.4rem 0.75rem', borderRadius: 6, border: 'none', background: '#2563eb', color: '#fff', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}
          >
            <FileType style={{ width: 13, height: 13 }} /> Download DOCX
          </Link>
        </div>
      </div>

      {/* Warnings bar */}
      {warnings.length > 0 && (
        <div className="preview-shell" style={{ background: '#78350f', color: '#fef3c7', padding: '0.5rem 1.5rem', fontSize: 12 }}>
          {warnings.map((w, i) => <span key={i} style={{ marginRight: 16 }}>⚠ {w.message}</span>)}
        </div>
      )}

      {/* Page simulation */}
      <div style={{ padding: '2rem 1rem', maxWidth: 900, margin: '0 auto' }}>
        {pages.map((pg, i) => (
          <div key={i}>
            <div className="preview-shell" style={{ fontSize: 10, color: '#9ca3af', textAlign: 'center', marginBottom: 4, fontFamily: 'sans-serif' }}>
              Page {pg.num} {pg.type !== 'chapter' ? `— ${pg.type.toUpperCase()}` : `— Chapter ${pg.chapterNum}`}
            </div>
            <PreviewPage number={pg.num} title={pg.title} isOverflowing={pg.isOverflowing}>
              {pg.html}
            </PreviewPage>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Preview HTML builders ─────────────────────────────────────────────────────

function buildFrontMatterPreview(fm, thesis) {
  return `
<div style="text-align:center;padding:1rem 0">
  <p style="font-size:14pt;font-weight:bold;text-transform:uppercase;line-height:1.4;margin-bottom:1rem">${thesis?.title || ''}</p>
  <p style="margin-bottom:0.5rem">A Thesis Submitted for the Degree of</p>
  <p style="font-weight:bold;margin-bottom:0.75rem">${fm?.degree || 'Doctor of Philosophy'} in ${fm?.specialization || ''}</p>
  <p style="margin-bottom:0.25rem">Submitted by: <strong>${fm?.student_name || '[Student Name]'}</strong></p>
  <p style="margin-bottom:0.25rem">Reg. No.: ${fm?.reg_number || '[Reg. No.]'}</p>
  <p style="margin-top:0.75rem">Under the supervision of: <strong>${fm?.supervisor_name || '[Supervisor Name]'}</strong></p>
  <p style="margin-top:1rem;font-weight:bold">${fm?.university_name || '[University Name]'}</p>
  <p>${fm?.faculty_name || '[Faculty]'}</p>
  <p>${(fm?.submission_month || '[Month]') + ' ' + (fm?.submission_year || '[Year]')}</p>
</div>`
}

function buildTOCPreview(chapters) {
  const sorted = [...chapters].sort((a, b) => (a.chapter_number || 0) - (b.chapter_number || 0))
  return `
<h1 style="text-align:center;font-size:13pt;font-weight:bold;text-transform:uppercase;margin-bottom:1rem">Table of Contents</h1>
${sorted.map(ch => `<p style="text-indent:0;margin-bottom:0.2rem"><strong>Chapter ${ch.chapter_number}:</strong> ${ch.title}</p>`).join('')}
<p style="text-indent:0;margin-bottom:0.2rem"><strong>References</strong></p>`
}

function buildLOFPreview(figures) {
  const entries = [...(figures || new Map()).values()].filter(f => f.type !== 'table')
  if (!entries.length) return ''
  return `
<h1 style="text-align:center;font-size:13pt;font-weight:bold;text-transform:uppercase;margin-bottom:1rem">List of Figures</h1>
${entries.map(f => `<p style="text-indent:0;margin-bottom:0.2rem">${f.displayLabel}: ${f.title || ''}</p>`).join('')}`
}

function buildLOTPreview(tables) {
  const entries = [...(tables || new Map()).values()]
  if (!entries.length) return ''
  return `
<h1 style="text-align:center;font-size:13pt;font-weight:bold;text-transform:uppercase;margin-bottom:1rem">List of Tables</h1>
${entries.map(t => `<p style="text-indent:0;margin-bottom:0.2rem">${t.displayLabel}: ${t.title || ''}</p>`).join('')}`
}
